import { createContext, type ReactNode, useContext } from 'react';
import clsx from 'clsx';
import { useToastState, useToastItemExit, type Toast, type ToastType } from '../hooks/useToastState';

export type { Toast, ToastType };

interface ToastContextValue {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Hook to access toast functionality.
 *
 * @throws {Error} If used outside ToastProvider
 *
 * @example
 * ```tsx
 * const { showToast } = useToast();
 *
 * const handleSuccess = () => {
 *   showToast({
 *     type: 'success',
 *     message: 'Tenant created successfully!',
 *     duration: 3000,
 *   });
 * };
 *
 * const handleError = () => {
 *   showToast({
 *     type: 'error',
 *     message: 'Failed to create tenant. Please try again.',
 *     action: {
 *       label: 'Retry',
 *       onClick: () => handleRetry(),
 *     },
 *   });
 * };
 * ```
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  /** Maximum number of toasts to display at once (default: 5) */
  maxToasts?: number;
}

/**
 * Provider component for toast notifications.
 *
 * Must wrap your app or the part of the tree that needs toast functionality.
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export const ToastProvider = ({ children, maxToasts = 5 }: ToastProviderProps) => {
  const { toasts, showToast, dismissToast } = useToastState(maxToasts);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

const ToastContainer = ({ toasts, onDismiss }: ToastContainerProps) => {
  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const ToastItem = ({ toast, onDismiss }: ToastItemProps) => {
  const { isExiting, handleDismiss } = useToastItemExit(toast.id, onDismiss);

  // Type-specific styles
  const typeConfig = {
    success: {
      bgColor: 'bg-success',
      textColor: 'text-white',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    error: {
      bgColor: 'bg-error',
      textColor: 'text-white',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    warning: {
      bgColor: 'bg-warning',
      textColor: 'text-white',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    info: {
      bgColor: 'bg-info',
      textColor: 'text-white',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  };

  const config = typeConfig[toast.type];

  return (
    <div
      className={clsx(
        'flex items-center gap-3 min-w-[320px] max-w-md px-4 py-3 rounded-lg shadow-lg',
        'pointer-events-auto',
        'transition-all duration-200',
        config.bgColor,
        config.textColor,
        isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0',
        'animate-[slideIn_200ms_ease-out]',
      )}
      role="alert"
    >
      {/* Icon */}
      <div className="flex-shrink-0">{config.icon}</div>

      {/* Message */}
      <div className="flex-1 text-body-sm font-medium">{toast.message}</div>

      {/* Action button */}
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action!.onClick();
            handleDismiss();
          }}
          className={clsx(
            'px-3 py-1 text-body-sm font-medium rounded',
            'bg-white/20 hover:bg-white/30',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-white/50',
          )}
        >
          {toast.action.label}
        </button>
      )}

      {/* Close button */}
      <button
        type="button"
        onClick={handleDismiss}
        className={clsx(
          'flex-shrink-0 p-1 rounded',
          'hover:bg-white/20',
          'transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-white/50',
        )}
        aria-label="Close notification"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Animation styles */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
